import { extractDisplayWeight } from './weightUtils'
import { getDisplayUnit } from './weightDisplay'
import { DEFAULT_SECONDARY_HAIRCUT_FACTOR, DEFAULT_PURITY_PRECISION, DEFAULT_MARGIN_OF_ERROR, XRF_CORE_METALS, DEFAULT_PGM_HAIRCUT_FACTOR, PGM_METALS } from './constants'
import { getLowestMetalPercentage, calculateDoreCorrection } from './fireAssayCalculations'
import { calculateAverage } from './statisticalCalculations'

/**
 * Extract numeric weight value from bar using context-aware weight utilities
 * @param {Number|Object} weight - Weight value (numeric or rich weight object)
 * @returns {Number} - Numeric weight value or 0
 */
function extractBarWeight(weight) {
  const { unit } = getDisplayUnit('bar')
  const extracted = extractDisplayWeight(weight, unit)
  return extracted ?? 0
}


/**
 * Generic cross-bar weighted/flat averaging for any assay type
 * Eliminates ~80% code duplication between XRF and fire implementations
 * @param {Object} resultsMap - Keyed by barNumber → bar-specific results
 * @param {Array} bars - Array of bar objects with barNumber and grossWeight
 * @param {Function} extractPurities - Type-specific extraction function (resultsForBar) => {AU, AG, PT, PD}
 * @param {String} primaryMetal - Primary metal symbol
 * @param {Boolean} useWeighted - Whether to use weighted averaging
 * @param {Number} precision - Purity precision (backend format, 4 = 2 decimal places displayed)
 * @param {Number} marginOfError - Margin of error multiplier (default 1.0 = no margin) - only applied for fire assays
 * @returns {Object} - {availableMetals, primaryMetal, primary, AU, AG, PT, PD}
 */
function calculateGenericCrossBarAverages(
  resultsMap,
  bars,
  extractPurities,
  primaryMetal,
  useWeighted,
  precision,
  marginOfError = 1.0
) {
  // Extract purities using provided extraction function
  const barPurities = bars.map(bar => {
    const barNumber = String(bar.barNumber)
    const resultsForBar = resultsMap[barNumber] || {}
    const purities = extractPurities(resultsForBar)
    return {
      barNumber: parseInt(barNumber),
      barWeight: extractBarWeight(bar.grossWeight),
      hasWeight: extractBarWeight(bar.grossWeight) > 0,
      purities
    }
  }).filter(b => Object.values(b.purities).some(p => p !== null))

  if (!barPurities.length) {
    return { availableMetals: [], primaryMetal, primary: null }
  }

  // Calculate averages per metal (core XRF + PGM optionals IR/RH/RU)
  const metalSymbols = [...new Set([...XRF_CORE_METALS, ...PGM_METALS])]
  const results = { availableMetals: [], primaryMetal: primaryMetal.toUpperCase() }
  
  // First pass: determine which metals were measured across ANY bar
  const measuredMetals = new Set()
  barPurities.forEach(barData => {
    metalSymbols.forEach(symbol => {
      if (barData.purities[symbol] !== null && barData.purities[symbol] !== undefined) {
        measuredMetals.add(symbol)
      }
    })
  })
  
  metalSymbols.forEach(symbol => {
    const barsWithMetal = barPurities.filter(b => b.purities[symbol] !== null)
    
    // Include metal in availableMetals if it was measured in ANY bar
    if (measuredMetals.has(symbol)) {
      results.availableMetals.push(symbol)
    }
    
    if (!barsWithMetal.length) {
      results[symbol] = null
      return
    }
    
    let rawAverage = null
    if (useWeighted) {
      const weightsAvailable = barsWithMetal.filter(b => b.hasWeight)
      if (weightsAvailable.length > 0) {
        const totalWeighted = weightsAvailable.reduce((sum, b) => 
          sum + (b.purities[symbol] * b.barWeight), 0
        )
        const totalWeight = weightsAvailable.reduce((sum, b) => sum + b.barWeight, 0)
        rawAverage = totalWeight > 0 ? totalWeighted / totalWeight : null
      } else {
        rawAverage = barsWithMetal.reduce((sum, b) => sum + b.purities[symbol], 0) / barsWithMetal.length
      }
    } else {
      rawAverage = barsWithMetal.reduce((sum, b) => sum + b.purities[symbol], 0) / barsWithMetal.length
    }
    
    // Apply margin of error BEFORE rounding (mirrors backend calculation)
    // This ensures frontend shows same values as backend after save
    if (rawAverage !== null) {
      rawAverage = rawAverage * marginOfError
    }
    
    // Apply precision rounding (mirrors backend logic)
    // Backend stores as decimal (0-1), frontend works in percentage (0-100)
    // So subtract 2: precision=4 on 0.9287 = 2 decimals on 92.87%
    const displayPrecision = Math.max(0, precision - 2)
    results[symbol] = rawAverage !== null ? Number(rawAverage.toFixed(displayPrecision)) : null
  })
  
  results.primary = results[results.primaryMetal] || null
  return results
}

/**
 * Fire-specific purity extraction
 * CORRECTED: Primary from cup only (doré measures total precious metals, not gold alone)
 * For AU assays: gold from cup, silver from doré differential
 * For AG assays: silver from cup (doré is pure silver control shot, cannot derive gold)
 * @param {Object} fireResults - Fire results for this bar {sample1: {gross, net}, control: {...}}
 * @param {String} primaryMetal - Primary metal symbol (AU or AG) - determines which metal is primary
 * @param {Number} secondaryHaircutFactor - Haircut factor for secondary metal from doré (default: 0.95)
 * @param {Number} marginOfError - Margin of error to apply to cup purity before differential (default: 1.0)
 * @returns {Object} - {AU, AG, PT, PD}
 */
function extractFirePurities(fireResults, primaryMetal = 'AU', secondaryHaircutFactor = DEFAULT_SECONDARY_HAIRCUT_FACTOR, marginOfError = 1.0) {
  // Fire doesn't measure PGMs, only AU/AG
  // CORRECTED: Primary from cup only, doré used for secondary metal differential
  if (!fireResults || typeof fireResults !== 'object') {
    return { AU: null, AG: null, PT: null, PD: null }
  }
  
  const purities = []
  
  // Extract cup purities (all non-control samples)
  Object.entries(fireResults).forEach(([key, sample]) => {
    if (key === 'control' || !sample || sample.outlier) return
    
    const grossWeight = sample.gross?.grams || sample.gross?.micrograms / 1000000 || 0
    const netWeight = sample.net?.grams || sample.net?.micrograms / 1000000 || 0
    
    if (grossWeight > 0) {
      // Round each individual purity to match backend storage precision (0.8889 → 88.89%)
      // Backend stores as NUMERIC(5,4) on 0-1 scale = 2 decimals on percentage scale
      const rawPurity = (netWeight / grossWeight) * 100
      const roundedPurity = Number(rawPurity.toFixed(2))
      purities.push(roundedPurity)
    }
  })
  
  // Calculate cup average
  const cupPurity = purities.length > 0 
    ? purities.reduce((sum, p) => sum + p, 0) / purities.length 
    : null
  
  // Extract doré purity (total precious metals)
  // CRITICAL: Doré measures TOTAL precious metals (Au+Ag combined), NOT gold alone
  // For AU assays: doré differential gives secondary silver content
  // For AG assays: doré used as control standard for gold differential
  let dorePurity = null
  const control = fireResults.control
  const useDoreCorrection = control?.useDoreCorrection || false
  
  if (control) {
    const grossWeight = control.gross?.grams || control.gross?.micrograms / 1000000 || 0
    const netWeight = control.net?.grams || control.net?.micrograms / 1000000 || 0
    
    if (grossWeight > 0) {
      // Doré measures TOTAL precious metals (Au+Ag), not gold separately
      // Round to match backend storage precision
      const rawPurity = (netWeight / grossWeight) * 100
      dorePurity = Number(rawPurity.toFixed(2))
    }
  }
  
  let primaryPurity = null
  if (cupPurity !== null) {
    // Primary from cups (gold for AU assays, silver for AG assays)
    if (primaryMetal.toUpperCase() === 'AG' && useDoreCorrection && control) {
      const correctionFactor = calculateDoreCorrection(control)
      primaryPurity = cupPurity + (correctionFactor * 100)
    } else {
      primaryPurity = cupPurity
    }
  } else if (dorePurity !== null) {
    // Fallback: only doré available (edge case)
    primaryPurity = dorePurity
  }
  
  // Calculate secondary metal from doré differential when correction is enabled
  // For AU assays: silver = (dore - margin_adjusted_gold) * haircut
  // For AG assays: cannot derive gold as secondary, since silver dore is pure silver control shot
  // NOTE: Secondary differential is NOT margined - only the cup primary metal used in the calculation is margined
  let secondaryPurity = null
  if (primaryMetal.toUpperCase() === 'AU' && useDoreCorrection && control && cupPurity !== null && dorePurity !== null) {
    // For secondary calculation: use margin-adjusted raw cupPurity (not corrected with doré factor)
    // This matches per-bar calculation which uses raw samplePurity for secondary
    const marginAdjustedCupPurity = cupPurity * marginOfError
    secondaryPurity = Math.max(0, (dorePurity - marginAdjustedCupPurity) * secondaryHaircutFactor)
  }
  
  if (primaryMetal.toUpperCase() === 'AG') {
    // AG assay: silver is primary (from cups, represents % of total bar), no secondary gold from doré
    return {
      AU: null,
      AG: primaryPurity,
      PT: null,
      PD: null
    }
  } else {
    // AU assay: gold is primary (from cups), silver is secondary (from doré differential)
    return {
      AU: primaryPurity,    // Gold from cups
      AG: secondaryPurity,  // Silver from doré differential
      PT: null,
      PD: null
    }
  }
}

/**
 * XRF-specific purity extraction
 * @param {Object} xrfResults - XRF results for this bar {AU: [95.1, 94.8], AG: [3.2]}
 * @param {Number} pgmHaircutFactor - PGM haircut factor to apply to lowest PT/PD reading (default: 1.0)
 * @returns {Object} - {AU, AG, PT, PD, CU}
 */
function extractXrfPurities(xrfResults, pgmHaircutFactor = DEFAULT_PGM_HAIRCUT_FACTOR) {
  // For AU/AG: average all readings
  const getAvgPurity = (metalArray) => {
    const result = calculateAverage(metalArray || [])
    return result != null && result > 0 ? result : null
  }
  
  // For CU: use lowest reading
  const getLowestPurity = (metalArray) => {
    const result = getLowestMetalPercentage(metalArray || [])
    return result > 0 ? result : null
  }
  
  // For PGMs: average all readings, but apply haircut to minimum before averaging
  const getPgmPurity = (metalArray) => {
    const readings = (metalArray || []).filter(r => r != null && !isNaN(r) && r > 0)
    if (readings.length === 0) return null
    
    const minReading = Math.min(...readings)
    const adjustedAvg = readings.reduce((sum, reading) => {
      const adjustedReading = reading === minReading ? reading * pgmHaircutFactor : reading
      return sum + adjustedReading
    }, 0) / readings.length
    
    return adjustedAvg > 0 ? adjustedAvg : null
  }

  const result = {
    AU: getAvgPurity(xrfResults.AU),
    AG: getAvgPurity(xrfResults.AG),
    CU: getLowestPurity(xrfResults.CU),
  }
  for (const metal of PGM_METALS) {
    result[metal] = getPgmPurity(xrfResults[metal])
  }
  return result
}

/**
 * Calculate cross-bar fire assay averages
 * @param {Object} fireResults - Keyed by barNumber → fire results
 * @param {Array} bars - Array of bar objects
 * @param {String} primaryMetal - Primary metal symbol
 * @param {Boolean} useWeighted - Use weighted averaging
 * @param {Number} precision - Purity precision (backend format)
 * @param {Number} secondaryHaircutFactor - Haircut factor for secondary metal from doré (default: 0.95)
 * @param {Number} marginOfError - Margin of error multiplier (default 0.995) - mirrors backend
 * @returns {Object} - Cross-bar averages with metadata, including both raw and margin-adjusted values
 */
export function calculateFireCrossBarAverages(
  fireResults, 
  bars, 
  primaryMetal = 'AU', 
  useWeighted = true, 
  precision = DEFAULT_PURITY_PRECISION, 
  secondaryHaircutFactor = DEFAULT_SECONDARY_HAIRCUT_FACTOR,
  marginOfError = DEFAULT_MARGIN_OF_ERROR
) {
  const fireResultsToBarNumber = new Map()
  const resultsMap = bars.reduce((acc, bar) => {
    const barNumber = String(bar.barNumber)
    const barFireResults = fireResults[barNumber] || {}
    acc[barNumber] = barFireResults
    fireResultsToBarNumber.set(barFireResults, bar.barNumber)
    return acc
  }, {})
  
  // Calculate raw averages (no margin applied)
  const extractFirePuritiesRaw = (fireResultsForBar) => {
    return extractFirePurities(fireResultsForBar, primaryMetal, secondaryHaircutFactor, 1.0)
  }
  
  const rawAverages = calculateGenericCrossBarAverages(
    resultsMap,
    bars,
    extractFirePuritiesRaw,
    primaryMetal,
    useWeighted,
    precision,
    1.0  // No margin for raw calculation
  )
  
  // Calculate margin-adjusted averages (with margin applied)
  const extractFirePuritiesWithHaircutAndMargin = (fireResultsForBar) => {
    return extractFirePurities(fireResultsForBar, primaryMetal, secondaryHaircutFactor, marginOfError)
  }
  
  const marginAdjustedAverages = calculateGenericCrossBarAverages(
    resultsMap,
    bars,
    extractFirePuritiesWithHaircutAndMargin,
    primaryMetal,
    useWeighted,
    precision,
    marginOfError
  )
  
  // Combine results with both raw and margin-adjusted values
  const combined = { ...marginAdjustedAverages }
  
  // Add raw values with _raw suffix for each metal
  XRF_CORE_METALS.forEach(symbol => {
    combined[`${symbol}_raw`] = rawAverages[symbol]
  })
  combined.primary_raw = rawAverages.primary
  
  return combined
}

/**
 * Calculate cross-bar XRF averages
 * @param {Object} xrfResults - Keyed by barNumber → per-metal results {AU: [95.1, 94.8], AG: [3.2, 3.5]}
 * @param {Array} bars - Array of bar objects with barNumber and grossWeight properties
 * @param {String} primaryMetal - Primary metal symbol (AU, AG, PT, PD) - defaults to 'AU'
 * @param {Boolean} useWeighted - Whether to use weighted averaging - defaults to true
 * @param {Number} precision - Decimal places for rounding purity percentages - defaults to 4
 * @param {Number} pgmHaircutFactor - PGM haircut factor to apply to lowest PT/PD reading (default: 1.0)
 * @returns {Object} - Cross-bar averages for all metals plus metadata
 */
export function calculateXrfCrossBarAverages(xrfResults, bars, primaryMetal = 'AU', useWeighted = true, precision = DEFAULT_PURITY_PRECISION, pgmHaircutFactor = DEFAULT_PGM_HAIRCUT_FACTOR) {
  const marginOfError = 1.0
  const extractXrfPuritiesWithHaircut = (resultsForBar) => extractXrfPurities(resultsForBar, pgmHaircutFactor)
  return calculateGenericCrossBarAverages(
    xrfResults,
    bars,
    extractXrfPuritiesWithHaircut,
    primaryMetal,
    useWeighted,
    precision,
    marginOfError
  )
}

/**
 * @deprecated Use calculateXrfCrossBarAverages instead
 * Kept for backward compatibility with existing code
 */
export function calculateCrossBarAverages(xrfResults, bars, primaryMetal = 'AU', useWeightedXrfAveraging = true, purityPrecision = DEFAULT_PURITY_PRECISION, pgmHaircutFactor = DEFAULT_PGM_HAIRCUT_FACTOR) {
  return calculateXrfCrossBarAverages(xrfResults, bars, primaryMetal, useWeightedXrfAveraging, purityPrecision, pgmHaircutFactor)
}

/**
 * Check if weighted averaging can be used (all bars have weights > 0)
 * @param {Array} bars - Array of bar objects with grossWeight properties
 * @returns {Boolean} - True if all bars have meaningful weights
 */
export function canUseWeightedAveraging(bars) {
  return bars.length > 0 && bars.every(bar => extractBarWeight(bar.grossWeight) > 0)
}
